import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, ApiError } from './api.ts';

/**
 * 登录态。
 *
 * 单一数据源：所有组件通过这里拿 user 与登录动作，
 * 避免各组件各自 fetch `/api/auth/me` 造成竞态与重复请求。
 *
 * 未登录时**不渲染应用主体** —— 早期版本是渲染后再各自收到 401，
 * 结果是页面上同时闪着行情卡片与「请登录」，体验割裂。
 */

export interface AuthUser {
  id: number;
  username: string;
  displayName: string | null;
  role: 'user' | 'admin';
}

interface AuthState {
  /** null = 尚未确认（显示加载中）；false = 未登录；true = 已登录 */
  ready: boolean;
  user: AuthUser | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** 登录后需要跳回哪一页 */
  redirectTo: string | null;
  takeRedirect: () => string | null;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [ready, setReady] = useState(false);
  const [redirectTo, setRedirectTo] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.me()
      .then((r) => { if (!cancelled) setUser(r.user); })
      .catch((e) => {
        // 401 是正常状态（首次访问即未登录），不当作错误处理
        if (!cancelled && !(e instanceof ApiError && e.status === 401)) {
          console.warn('[auth] 登录态检查失败', e);
        }
      })
      .finally(() => { if (!cancelled) setReady(true); });
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const r = await api.login(username, password);
    setUser(r.user);
  }, []);

  const logout = useCallback(async () => {
    try { await api.logout(); } finally { setUser(null); }
  }, []);

  /**
   * 任意请求收到 401 → 立刻清空本地用户态。
   * 由 api.ts 广播事件驱动，好处是「会话过期」这件事只有一个处理点：
   * 无论它是首个轮询、某个笔记保存、还是打开页面时的 me 检查发现的。
   */
  useEffect(() => {
    const onUnauthorized = (): void => setUser(null);
    window.addEventListener('stocknote:unauthorized', onUnauthorized);
    return () => window.removeEventListener('stocknote:unauthorized', onUnauthorized);
  }, []);

  const takeRedirect = useCallback(() => {
    const v = redirectTo;
    setRedirectTo(null);
    return v;
  }, [redirectTo]);

  const value = useMemo<AuthState>(
    () => ({ ready, user, login, logout, redirectTo, takeRedirect }),
    [ready, user, login, logout, redirectTo, takeRedirect],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth 必须在 AuthProvider 内使用');
  return ctx;
}

/** 是否管理员（决定手动同步按钮是否可见） */
export const isAdmin = (u: AuthUser | null): boolean => u?.role === 'admin';
